import React, { createContext, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useValidateLogin } from '../service/auth.service';
import { notification } from 'antd';
import { queryClient } from '../../utils/queryClient';
import { cleanupDraftNamespace } from '../shared/drafts/draftStorage';
import _groupBy from 'lodash/groupBy';
const _ = { groupBy: _groupBy };

const SelectedUserContext = createContext();

const safeJSONParse = (data, fallback) => {
    try {
        return JSON.parse(data);
    } catch (e) {
        return fallback;
    }
};

export const SelectedUserProvider = ({ children }) => {

    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const [hospital, setHospital] = useState(JSON.parse(sessionStorage?.getItem('hmsv2-hospital') || null));
    const [loginRole, setLoginRole] = useState(JSON.parse(sessionStorage?.getItem('hmsv2-role') || null));
    const [loginUser, setLoginUser] = useState(JSON.parse(sessionStorage?.getItem('admin-user') || null));
    const [loginBranch, setLoginBranch] = useState(JSON.parse(sessionStorage?.getItem('hmsv2-branch') || null));
    const [loginFeatures, setLoginFeatures] = useState(JSON.parse(sessionStorage?.getItem('admin-feature') || null));
    const [userToken, setUserToken] = useState(sessionStorage.getItem('admin-token'));
    const [menuList, setMenuList] = useState({
        'mainMenus': JSON.parse(sessionStorage?.getItem('hmsv2-mainMenu')) || {},
        'menus': JSON.parse(sessionStorage?.getItem('hmsv2-menu')) || [],
        'submenus': JSON.parse(sessionStorage?.getItem('hmsv2-submenu')) || []
    });

    const [assignedBranches, setAssignedBranches] = useState(JSON.parse(sessionStorage?.getItem('hmsv2-branches') || null));

    const [menuItem, setMenuItem] = useState('');
    const [selectedUser, setSelectedUser] = useState(JSON.parse(sessionStorage.getItem('selectedUser') || null));
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(JSON.parse(sessionStorage.getItem('selectedPatient') || null));
    const [selectedVisit, setSelectedVisit] = useState(JSON.parse(sessionStorage.getItem('selectedVisit') || null));
    const [selectedInsurance, setSelectedInsurance] = useState(safeJSONParse(sessionStorage.getItem('selectedInsurance') || null));
    const [selectedHospital, setSelectedHospital] = useState(JSON.parse(sessionStorage.getItem('selectedHospital') || null));
    const [selectedAcl, setSelectedAcl] = useState(JSON.parse(sessionStorage.getItem('selectedAcl') || null));

    const { mutate: validateLogin, isLoading: isLoggingIn } = useValidateLogin();

    const applyLoginSuccess = (axiosResponse) => {
        const loginData = axiosResponse?.data;

        if (!loginData?.user) {
            notification.error({ message: 'Server offline...' });
            return;
        }

        if (!['ACTIVE', 'NEW'].includes(loginData.user.userStatus)) {
            notification.warning({ message: 'Cannot login, user not active...' });
            return;
        }

        notification.success({ message: 'Successfully logged in' });

        // Always reset branch selection on a fresh login so every user
        // (including multi-branch users) goes through LockScreen.
        sessionStorage.removeItem('hmsv2-branch');
        sessionStorage.removeItem('hmsv2-branch-manual');
        setLoginBranch(null);

        const groupedMainMenu = _.groupBy(loginData.mainMenu || [], 'orderId');

        sessionStorage.setItem('hmsv2-hospital', JSON.stringify(loginData.hospital));
        sessionStorage.setItem('hmsv2-role', JSON.stringify(loginData.role));
        sessionStorage.setItem('hmsv2-branches', JSON.stringify(loginData.branches || []));
        sessionStorage.setItem('admin-user', JSON.stringify(loginData.user));
        sessionStorage.setItem('admin-feature', JSON.stringify(loginData.accessControlList));
        sessionStorage.setItem('admin-token', loginData.token);

        sessionStorage.setItem('hospitalId', loginData.hospital.hospitalId);
        sessionStorage.setItem('admin-name', `${loginData.user.firstName} ${loginData.user.lastName}`);
        sessionStorage.setItem('admin-username', loginData.user.username);
        sessionStorage.setItem('admin-role', loginData.role.roleName);
        sessionStorage.setItem('userId', loginData.user.userId);

        sessionStorage.setItem('hmsv2-mainMenu', JSON.stringify(groupedMainMenu));
        sessionStorage.setItem('hmsv2-menu', JSON.stringify(loginData.menus));
        sessionStorage.setItem('hmsv2-submenu', JSON.stringify(loginData.submenus));

        setHospital(loginData.hospital);
        setLoginRole(loginData.role);
        setLoginUser(loginData.user);
        setAssignedBranches(loginData.branches || []);
        setLoginFeatures(loginData.accessControlList);
        setUserToken(loginData.token);
        setMenuList({
            mainMenus: groupedMainMenu,
            menus: loginData.menus,
            submenus: loginData.submenus
        });
    };

    const handleLoginUser = (payload) => {
        validateLogin(
            {
                email: payload.username,
                password: payload.password
            },
            {
                onSuccess: applyLoginSuccess,
                onError: (err) => {
                    const description =
                        err?.response?.data?.message ||
                        err?.message ||
                        'Check credentials and try again...';
                    notification.error({
                        message: 'Login failed',
                        description: String(description),
                    });
                }
            }
        );
    };

    const findSubmenuList = (data, mainMenuId, menuId) => {
        for (const key in data) {
            const entries = data[key];
            for (const entry of entries) {
                if (entry.mainMenuId === mainMenuId && entry.menuId === menuId) {
                    return entry.submenuList;
                }
            }
        }

        return null; // Return null if not found
    };

    const handleLogoutUser = () => {
        if (isLoggingOut) {
            return;
        }
        setIsLoggingOut(true);

        // Show the spinner briefly on the logout control, then end the session
        // and route to sign-in using client-side navigation (no hard reload).
        window.setTimeout(() => {
            setLoginUser(null);
            setUserToken(null);
            setLoginFeatures(null);
            setLoginBranch(null);
            setMenuList(null);

            queryClient.clear();
            cleanupDraftNamespace();

            [
                'admin-user',
                'hmsv2-branch',
                'hmsv2-branch-manual',
                'admin-token',
                'admin-feature',
                'hmsv2-mainMenu',
                'hmsv2-menu',
                'hmsv2-submenu',
                'hmsv2-hospital',
                'hmsv2-role',
                'hmsv2-branches',
                'hmsv2-dashboard-cache',
                'hospitalId',
                'admin-name',
                'admin-username',
                'admin-role',
                'userId',
            ].forEach((key) => sessionStorage.removeItem(key));

            navigate('/', { replace: true });
            setIsLoggingOut(false);
        }, 300);
    }

    return (
        <SelectedUserContext.Provider value={{
            menuItem, setMenuItem, selectedUser, setSelectedUser, setHospital,
            loginUser, setLoginUser, userToken, hospital, loginRole, selectedInsurance, setSelectedInsurance,
            menuList, handleLoginUser, handleLogoutUser, loginFeatures, setMenuList,
            selectedPatient, setSelectedPatient, selectedRole, setSelectedRole,
            assignedBranches, loginBranch, setLoginBranch, selectedVisit, setSelectedVisit,
            selectedHospital, setSelectedHospital, selectedAcl, setSelectedAcl, findSubmenuList, isLoggingIn, isLoggingOut
        }}>
            {children}
        </SelectedUserContext.Provider>
    );
};

SelectedUserProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useSelectedUser = () => {
    return useContext(SelectedUserContext);
}
